import React from 'react';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import Icon from '../Icon';
import translations from './translations';
import { FormattedMessage } from 'react-intl';

const SortOptionSelectionItemContainer = styled(Paper)`
  && {
    margin-bottom: ${props => props.theme.spacing(2)}px;
  }
`;

const SortOptionSelectionHeaderNew = styled.div`
  color: #999;
  cursor: pointer;
  padding: ${props => props.theme.spacing()}px;
`;

const SortOptionSelectionHeader = styled.div`
  align-items: center;
  cursor: pointer;
  display: grid;
  width: 100%;
  grid-template-columns: 30px repeat(2, 1fr) 30px;
  grid-column-gap: ${props => props.theme.spacing()}px;
  padding: ${props => props.theme.spacing()}px;
`;

const SortOptionSelectionGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column-gap: ${props => props.theme.spacing()}px;
  padding: ${props => props.theme.spacing()}px;
`;

const SortOptionSelectionGridItem = styled.div`
  border: 1px solid #ccc;
  padding: ${props => props.theme.spacing()}px;
`;

const SortOptionSelectionColumn = styled.div`
  background: ${props => (props.isActive ? '#999' : '#e0e0e0')};
  color: ${props => (props.isActive ? '#fff' : '#000')};
  cursor: pointer;
  border: 1px solid #ccc;
  display: flex;
  margin-bottom: ${props => props.theme.spacing()}px;
  padding: ${props => props.theme.spacing()}px;
`;

const SortDirectionSelection = styled.div`
  background: ${props => (props.isActive ? '#999' : '#e0e0e0')};
  color: ${props => (props.isActive ? '#fff' : '#000')};
  cursor: pointer;
  border: 1px solid #ccc;
  display: flex;
  margin-bottom: ${props => props.theme.spacing()}px;
  padding: ${props => props.theme.spacing()}px;
`;

const SortOptionSelectionActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing()}px;
`;

const DataGridSortSelectionItem = ({ isOpen, sort }) => {
  const { sorting = {} } = useDataSourceContext();
  const {
    sortSelection,
    setSortSelection,
    getSortOptionName,
    getAvailableSortOptions,
    clearSortSelection,
    saveSortSelection,
    removeActiveSorting,
  } = sorting;
  const allowedDirections = ['asc', 'desc'];

  /**
   * Handles loading the filter selection based on the current filter.
   */
  const handleLoadSortSelection = () => {
    if (!sort.isNew) {
      setSortSelection({ ...sort, currentKey: sort.key, isNew: false });
    } else {
      const availableSortOptions = getAvailableSortOptions();
      const [firstSortOption] = availableSortOptions;

      if (firstSortOption) {
        setSortSelection({
          key: firstSortOption.key,
          direction: 'asc',
          currentKey: null,
          isNew: true,
        });
      }
    }
  };

  /**
   * Set the sort key of the sort selection based on the provided sort option.
   * @param {object} sortOption
   */
  const handleSelectAvailableSortOption = sortOption => () => {
    if (sortSelection.key === sortOption.key) {
      return;
    }

    setSortSelection({ ...sortSelection, key: sortOption.key });
  };

  /**
   * Set the sort direction of the sort selection based on the provided direction.
   * @param {string} sortDirection
   */
  const handleSelectSortDirection = sortDirection => () => {
    if (sortSelection.direction === sortDirection) {
      return;
    }

    setSortSelection({ ...sortSelection, direction: sortDirection });
  };

  /**
   * Handles closing and clearing the sort selection.
   */
  const handleCloseSortSelection = () => {
    clearSortSelection();
  };

  /**
   * Handles saving the current sort selection.
   */
  const handleSaveSortSelection = () => {
    saveSortSelection();
  };

  /**
   * Remove the active sorting if exists and clear the sort selection.
   */
  const handleRemoveSortSelection = () => {
    if (!sort.isNew) {
      removeActiveSorting(sort.key);
    }

    clearSortSelection();
  };

  return (
    <SortOptionSelectionItemContainer>
      {!isOpen && !sort.isNew && (
        <SortOptionSelectionHeader
          onClick={handleLoadSortSelection}
          onKeyPress={handleLoadSortSelection}
          role="button"
          tabIndex="0"
        >
          <div>
            <Icon icon={['fal', 'grip-lines']} size="small" />
          </div>
          <div>{getSortOptionName(sort.key)}</div>
          <div>
            {sort.direction && (
              <FormattedMessage {...translations.sortDirections[sort.direction]} />
            )}
          </div>
        </SortOptionSelectionHeader>
      )}

      {!isOpen && sort.isNew && (
        <SortOptionSelectionHeaderNew
          onClick={handleLoadSortSelection}
          onKeyPress={handleLoadSortSelection}
          role="button"
          tabIndex="0"
        >
          <FormattedMessage {...translations.sortOptionSelectionItem.newSortOptionHeader} />
        </SortOptionSelectionHeaderNew>
      )}

      {isOpen && (
        <SortOptionSelectionHeader
          onClick={handleCloseSortSelection}
          onKeyPress={handleCloseSortSelection}
          role="button"
          tabIndex="0"
        >
          <div>
            {!sortSelection.isNew && !isOpen && <Icon icon={['fal', 'grip-lines']} size="small" />}
          </div>
          <div>{getSortOptionName(sortSelection.key)}</div>
          <div>
            {sortSelection.direction && (
              <FormattedMessage {...translations.sortDirections[sortSelection.direction]} />
            )}
          </div>
          <div>
            <IconButton size="small" onClick={handleRemoveSortSelection}>
              <Icon icon={['fal', 'trash-alt']} size="small" />
            </IconButton>
          </div>
        </SortOptionSelectionHeader>
      )}

      <Collapse in={isOpen}>
        {sortSelection && (
          <>
            <SortOptionSelectionGrid>
              <SortOptionSelectionGridItem>
                {getAvailableSortOptions().map(sortOption => (
                  <SortOptionSelectionColumn
                    key={sortOption.key}
                    isActive={sortSelection.key === sortOption.key}
                    onClick={handleSelectAvailableSortOption(sortOption)}
                    onKeyPress={handleSelectAvailableSortOption(sortOption)}
                    role="button"
                    tabIndex="0"
                  >
                    {sortOption.name}
                  </SortOptionSelectionColumn>
                ))}
              </SortOptionSelectionGridItem>
              <SortOptionSelectionGridItem>
                {allowedDirections.map(sortDirection => (
                  <SortDirectionSelection
                    key={sortDirection}
                    isActive={sortSelection.direction === sortDirection}
                    onClick={handleSelectSortDirection(sortDirection)}
                    onKeyPress={handleSelectSortDirection(sortDirection)}
                    role="button"
                    tabIndex="0"
                  >
                    <FormattedMessage {...translations.sortDirections[sortDirection]} />
                  </SortDirectionSelection>
                ))}
              </SortOptionSelectionGridItem>
            </SortOptionSelectionGrid>
            <SortOptionSelectionActions>
              <Button variant="outlined" onClick={handleCloseSortSelection}>
                <FormattedMessage {...translations.sortOptionSelectionItem.cancelButton} />
              </Button>
              <Button variant="contained" color="primary" onClick={handleSaveSortSelection}>
                <FormattedMessage
                  {...translations.sortOptionSelectionItem[
                    sortSelection.isNew ? 'createButton' : 'saveButton'
                  ]}
                />
              </Button>
            </SortOptionSelectionActions>
          </>
        )}
      </Collapse>
    </SortOptionSelectionItemContainer>
  );
};

DataGridSortSelectionItem.propTypes = {
  isOpen: PropTypes.bool,
  sort: PropTypes.object,
};

DataGridSortSelectionItem.defaultProps = {
  isOpen: false,
  sort: undefined,
};

export default DataGridSortSelectionItem;
